import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { proceedingParticipantsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface MergeArgs {
  destinationId: string;
  sourceId: string;
}

export function useMergeParticipants(onSuccess: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ destinationId, sourceId }: MergeArgs) =>
      proceedingParticipantsApi.mergeProceedingParticipants({
        mergeProceedingParticipantsRequest: { destinationId, sourceId },
      }),
    onSuccess: async () => {
      // The merge reassigns every claim that referenced the source participant as creditor or
      // representative, so the claim caches are stale too — not just the participants list.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.ProceedingParticipants] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ClientClaims] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ClaimById], refetchType: "active" }),
      ]);
      showSuccess(t("proceedingParticipants.merge.success"));
      onSuccess();
    },
  });
}
